import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, Sparkles, Loader2, Heart } from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("error");
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/subscription/checkout/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === "paid") {
        setPaymentInfo(response.data);
        setStatus("success");
        return;
      } else if (response.data.status === "expired") {
        setStatus("error");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking status:", error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus("error");
      }
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center px-6" data-testid="subscription-success-page">
      <div className="max-w-md w-full">
        {/* Checking */}
        {status === "checking" && (
          <Card className="glass-card rounded-3xl border-0">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
              </div>
              <h1 className="font-heading text-2xl font-semibold text-foreground mb-3">
                Processing Your Payment
              </h1>
              <p className="text-muted-foreground">
                Please wait while we confirm your subscription...
              </p>
            </CardContent>
          </Card>
        )}

        {/* Success */}
        {status === "success" && (
          <Card className="glass-card rounded-3xl border-0">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6 animate-breathe">
                <Sparkles className="w-10 h-10 text-primary" />
              </div>
              <h1 className="font-heading text-2xl font-semibold text-foreground mb-3">
                Welcome to Premium!
              </h1>
              <p className="text-muted-foreground mb-8">
                Your subscription is now active. You have full access to all 365 days 
                of transformative content and unlimited Spirit Chat.
              </p>
              
              <div className="bg-primary/5 rounded-2xl p-4 mb-8">
                <div className="flex items-center justify-center gap-2 text-primary mb-2">
                  <Check className="w-5 h-5" />
                  <span className="font-medium">Payment Confirmed</span>
                </div>
                {paymentInfo && (
                  <p className="text-sm text-muted-foreground">
                    Amount: ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                  </p>
                )}
              </div>

              <Button
                onClick={() => navigate("/dashboard")}
                className="btn-primary w-full"
                data-testid="go-to-dashboard-btn"
              >
                <Heart className="w-4 h-4 mr-2" />
                Start Your Journey
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Error */}
        {status === "error" && (
          <Card className="glass-card rounded-3xl border-0">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">😔</span>
              </div>
              <h1 className="font-heading text-2xl font-semibold text-foreground mb-3">
                Something Went Wrong
              </h1>
              <p className="text-muted-foreground mb-8">
                We couldn't confirm your payment. Don't worry - if you were charged, 
                please contact us and we'll sort it out.
              </p>
              
              <div className="space-y-3">
                <Button
                  onClick={() => navigate("/subscription")}
                  className="btn-primary w-full"
                  data-testid="try-again-btn"
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  variant="ghost"
                  className="w-full"
                  data-testid="go-to-dashboard-error-btn"
                >
                  Return to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer Message */}
        <p className="text-center text-sm text-muted-foreground mt-8">
          {status === "success" 
            ? "Thank you for believing in yourself ✨" 
            : "Questions? We're here to help."}
        </p>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
